import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

const SupportForm = () => {
    const [step, setStep] = useState(1); // 1 = Category Selection, 2 = Details
    const [category, setCategory] = useState('');
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        subject: '',
        severity: '',
        importance: '',
        message: ''
    });
    const [loading, setLoading] = useState(false);
    const [success, setSuccess] = useState(false);
    const [error, setError] = useState('');

    const categories = [
        {
            id: 'bug',
            title: 'Report a Bug',
            description: 'Found something that isn\'t working correctly?',
            icon: (
                <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
            ),
            color: 'red'
        },
        {
            id: 'feature',
            title: 'Request a Feature',
            description: 'Have an idea to make SwiftBidPro better?',
            icon: (
                <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                </svg>
            ),
            color: 'blue'
        },
        {
            id: 'general',
            title: 'General Support',
            description: 'Need help with your account or have questions?',
            icon: (
                <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M18.364 5.636l-3.536 3.536m0 5.656l3.536 3.536M9.172 9.172L5.636 5.636m3.536 9.192l-3.536 3.536M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-5 0a4 4 0 11-8 0 4 4 0 018 0z" />
                </svg>
            ),
            color: 'green'
        }
    ];

    const handleCategorySelect = (categoryId) => {
        setCategory(categoryId);
        const categoryTitles = {
            bug: 'Bug Report',
            feature: 'Feature Request',
            general: 'General Inquiry'
        };
        setFormData({ ...formData, subject: categoryTitles[categoryId] });
        setStep(2);
    };

    const handleChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError('');
        setSuccess(false);

        try {
            // Format message based on category
            let finalMessage = formData.message;
            if (category === 'bug' && formData.severity) {
                finalMessage = `Severity: ${formData.severity}\n\n${formData.message}`;
            } else if (category === 'feature' && formData.importance) {
                finalMessage = `Importance: ${formData.importance}\n\n${formData.message}`;
            }

            const response = await fetch('/api/contact', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    name: formData.name,
                    email: formData.email,
                    subject: formData.subject,
                    message: finalMessage
                }),
            });

            const data = await response.json();

            if (response.ok) {
                setSuccess(true);
                setFormData({ name: '', email: '', subject: '', severity: '', importance: '', message: '' });
                setTimeout(() => {
                    setStep(1);
                    setCategory('');
                    setSuccess(false);
                }, 3000);
            } else {
                setError(data.message || 'Failed to send message. Please try again.');
            }
        } catch (error) {
            setError('Network error. Please check your connection and try again.');
        } finally {
            setLoading(false);
        }
    };

    const getColorClasses = (color) => {
        const colors = {
            red: {
                bg: 'bg-red-50',
                border: 'border-red-200',
                hoverBorder: 'hover:border-red-400',
                icon: 'text-red-600',
                hoverBg: 'hover:bg-red-100'
            },
            blue: {
                bg: 'bg-blue-50',
                border: 'border-blue-200',
                hoverBorder: 'hover:border-blue-400',
                icon: 'text-blue-600',
                hoverBg: 'hover:bg-blue-100'
            },
            green: {
                bg: 'bg-green-50',
                border: 'border-green-200',
                hoverBorder: 'hover:border-green-400',
                icon: 'text-green-600',
                hoverBg: 'hover:bg-green-100'
            }
        };
        return colors[color];
    };

    return (
        <div className="bg-white rounded-lg shadow-lg p-6 sm:p-8 max-w-3xl mx-auto">
            <AnimatePresence mode="wait">
                {step === 1 && (
                    <motion.div
                        key="category"
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: 20 }}
                        transition={{ duration: 0.3 }}
                    >
                        <div className="text-center mb-8">
                            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">
                                How can we help you?
                            </h2>
                            <p className="text-gray-600">
                                Select the type of support you need
                            </p>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            {categories.map((cat) => {
                                const colors = getColorClasses(cat.color);
                                return (
                                    <button
                                        key={cat.id}
                                        onClick={() => handleCategorySelect(cat.id)}
                                        className={`${colors.bg} ${colors.border} ${colors.hoverBorder} ${colors.hoverBg} border-2 rounded-lg p-6 text-center transition-all duration-200 hover:shadow-md`}
                                    >
                                        <div className={`${colors.icon} mx-auto mb-4`}>
                                            {cat.icon}
                                        </div>
                                        <h3 className="font-semibold text-gray-900 mb-2">{cat.title}</h3>
                                        <p className="text-sm text-gray-600">{cat.description}</p>
                                    </button>
                                );
                            })}
                        </div>
                    </motion.div>
                )}

                {step === 2 && (
                    <motion.div
                        key="form"
                        initial={{ opacity: 0, x: 20 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: -20 }}
                        transition={{ duration: 0.3 }}
                    >
                        <div className="mb-6">
                            <button
                                onClick={() => setStep(1)}
                                className="text-primary-600 hover:text-primary-700 flex items-center text-sm font-medium"
                            >
                                <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
                                </svg>
                                Back to categories
                            </button>
                        </div>

                        <div className="text-center mb-6">
                            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">
                                {categories.find(c => c.id === category)?.title}
                            </h2>
                            <p className="text-gray-600">
                                Please provide details about your {category === 'bug' ? 'issue' : category === 'feature' ? 'idea' : 'question'}
                            </p>
                        </div>

                        {success && (
                            <motion.div
                                className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded-md mb-6"
                                initial={{ opacity: 0, scale: 0.95 }}
                                animate={{ opacity: 1, scale: 1 }}
                                transition={{ duration: 0.3 }}
                            >
                                <div className="flex items-center">
                                    <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                    </svg>
                                    <span>Message sent successfully! We'll get back to you soon.</span>
                                </div>
                            </motion.div>
                        )}

                        {error && (
                            <motion.div
                                className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-md mb-6"
                                initial={{ opacity: 0, scale: 0.95 }}
                                animate={{ opacity: 1, scale: 1 }}
                                transition={{ duration: 0.3 }}
                            >
                                <div className="flex items-center">
                                    <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                                    </svg>
                                    <span>{error}</span>
                                </div>
                            </motion.div>
                        )}

                        <form onSubmit={handleSubmit} className="space-y-4">
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <div>
                                    <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                                        Name *
                                    </label>
                                    <input
                                        type="text"
                                        id="name"
                                        name="name"
                                        value={formData.name}
                                        onChange={handleChange}
                                        required
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                                        placeholder="Your full name"
                                    />
                                </div>
                                <div>
                                    <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                                        Email *
                                    </label>
                                    <input
                                        type="email"
                                        id="email"
                                        name="email"
                                        value={formData.email}
                                        onChange={handleChange}
                                        required
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                                        placeholder="your.email@example.com"
                                    />
                                </div>
                            </div>

                            {category === 'bug' && (
                                <div>
                                    <label htmlFor="severity" className="block text-sm font-medium text-gray-700 mb-1">
                                        Severity (Optional)
                                    </label>
                                    <select
                                        id="severity"
                                        name="severity"
                                        value={formData.severity}
                                        onChange={handleChange}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                                    >
                                        <option value="">Select severity...</option>
                                        <option value="Low">Low - Minor inconvenience</option>
                                        <option value="Medium">Medium - Affects functionality</option>
                                        <option value="High">High - Blocking work</option>
                                        <option value="Critical">Critical - System down</option>
                                    </select>
                                </div>
                            )}

                            {category === 'feature' && (
                                <div>
                                    <label htmlFor="importance" className="block text-sm font-medium text-gray-700 mb-1">
                                        Importance (Optional)
                                    </label>
                                    <select
                                        id="importance"
                                        name="importance"
                                        value={formData.importance}
                                        onChange={handleChange}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                                    >
                                        <option value="">Select importance...</option>
                                        <option value="Nice to have">Nice to have</option>
                                        <option value="Would improve workflow">Would improve workflow</option>
                                        <option value="Very important">Very important</option>
                                        <option value="Critical for my business">Critical for my business</option>
                                    </select>
                                </div>
                            )}

                            <div>
                                <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-1">
                                    {category === 'bug' ? 'Describe the issue' : category === 'feature' ? 'Describe your idea' : 'Your message'} *
                                </label>
                                <textarea
                                    id="message"
                                    name="message"
                                    rows="6"
                                    value={formData.message}
                                    onChange={handleChange}
                                    required
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                                    placeholder={
                                        category === 'bug'
                                            ? "What happened? What were you trying to do? What did you expect to happen?"
                                            : category === 'feature'
                                                ? "What feature would you like to see? How would it help your workflow?"
                                                : "How can we help you?"
                                    }
                                />
                            </div>

                            <button
                                type="submit"
                                disabled={loading}
                                className="w-full bg-primary-600 text-white py-2 px-4 rounded-md hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed transition duration-200"
                            >
                                {loading ? (
                                    <div className="flex items-center justify-center">
                                        <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                        </svg>
                                        Sending...
                                    </div>
                                ) : (
                                    'Send Message'
                                )}
                            </button>
                        </form>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};

export default SupportForm;
